"use client";

import * as React from "react";
import { useParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { User, ExternalLink } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useGetAssignmentReportInfoQuery,
  useGetAssignmentReportDataQuery,
  useGetAssignmentReportResultsQuery,
} from "@/api/reports/publicReportApi";

import { TrustIcon } from "@/components/icons/generated/TrustIcon";
import { DownloadIcon } from "@/components/icons/generated/DownloadIcon";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { PublicStudentContent } from "@/components/report/PublicStudentContent";
import { ReportPrintView } from "@/components/report/ReportPrintView";
import { CertificateInfo } from "@/components/report/CertificateInfo";

const ReportPage: React.FC = () => {
  const params = useParams();
  const token = params.token as string;
  const t = useTranslations();

  const [selectedAttemptId, setSelectedAttemptId] = React.useState<number | undefined>();

  // Кнопку входа показываем только гостю (нет access_token). Считаем синхронно
  // на первом клиентском рендере — компонент "use client", localStorage доступен.
  const [hasToken] = React.useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return !!localStorage.getItem("access_token");
  });

  const printRef = React.useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = React.useState(false);

  const handlePrint = React.useCallback(async () => {
    const node = printRef.current;
    if (!node || isExporting) return;
    setIsExporting(true);

    const originalStyle = node.getAttribute("style") || "";
    // Temporarily make the off-screen .print-only container visible for capture
    node.setAttribute(
      "style",
      `${originalStyle}; position: static !important; left: auto !important; top: auto !important; opacity: 1 !important; pointer-events: auto !important; width: 800px !important;`
    );

    // Pre-inline images as data URLs so html-to-image doesn't re-fetch them
    // (which would hit CORS on the CDN). Keep original srcs to restore afterwards.
    const imgs = Array.from(node.querySelectorAll<HTMLImageElement>("img"));
    const originalSrcs = new Map<HTMLImageElement, string>();
    const removedImgs: Array<{ el: HTMLImageElement; parent: Node; next: Node | null }> = [];

    const fetchAsDataUrl = async (url: string): Promise<string | null> => {
      try {
        // Same-origin URLs (or already-data URLs) can be fetched directly;
        // cross-origin ones go through our server-side proxy to bypass CORS.
        const isSameOrigin = url.startsWith("/") || url.startsWith(window.location.origin);
        const fetchUrl = isSameOrigin
          ? url
          : `/api/proxy-image?url=${encodeURIComponent(url)}`;
        const response = await fetch(fetchUrl, { credentials: "omit" });
        if (!response.ok) return null;
        const blob = await response.blob();
        return await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        });
      } catch {
        return null;
      }
    };

    await Promise.all(
      imgs.map(async (img) => {
        if (!img.src || img.src.startsWith("data:")) return;

        // Swap to data URL if CDN allows CORS
        const dataUrl = await fetchAsDataUrl(img.src);
        if (dataUrl) {
          originalSrcs.set(img, img.src);
          img.src = dataUrl;
          // Wait for the new src to decode
          await new Promise<void>((resolve) => {
            if (img.complete && img.naturalWidth > 0) return resolve();
            const done = () => resolve();
            img.addEventListener("load", done, { once: true });
            img.addEventListener("error", done, { once: true });
            setTimeout(done, 3000);
          });
          return;
        }

        // Couldn't inline — remove from DOM so html-to-image doesn't throw on it
        if (img.parentNode) {
          removedImgs.push({ el: img, parent: img.parentNode, next: img.nextSibling });
          img.parentNode.removeChild(img);
        }
      })
    );

    try {
      const [{ toPng }, { default: jsPDF }] = await Promise.all([
        import("html-to-image"),
        import("jspdf"),
      ]);

      const dataUrl = await toPng(node, {
        pixelRatio: 2,
        backgroundColor: "#ffffff",
        cacheBust: true,
        // Skip broken / cross-origin images instead of failing the whole export
        imagePlaceholder:
          "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7",
        fetchRequestInit: { mode: "cors", credentials: "omit" },
      });

      const img = new Image();
      img.src = dataUrl;
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("Image load failed"));
      });

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const a4Width = pdf.internal.pageSize.getWidth();
      const a4Height = pdf.internal.pageSize.getHeight();
      const renderedWidth = a4Width;
      const renderedHeight = (img.height / img.width) * renderedWidth;

      if (renderedHeight <= a4Height) {
        pdf.addImage(dataUrl, "PNG", 0, 0, renderedWidth, renderedHeight);
      } else {
        let remaining = renderedHeight;
        let position = 0;
        while (remaining > 0) {
          pdf.addImage(dataUrl, "PNG", 0, position, renderedWidth, renderedHeight);
          remaining -= a4Height;
          position -= a4Height;
          if (remaining > 0) pdf.addPage();
        }
      }

      pdf.save(`Report-${token}.pdf`);
    } finally {
      // Restore original image srcs (swapped to data URLs for capture)
      for (const [img, src] of originalSrcs) {
        img.src = src;
      }
      // Restore images removed before capture
      for (const { el, parent, next } of removedImgs) {
        parent.insertBefore(el, next);
      }
      node.setAttribute("style", originalStyle);
      setIsExporting(false);
    }
  }, [isExporting, token]);

  // Fetch report info (basic data)
  const { data: reportInfo, isLoading, isError } = useGetAssignmentReportInfoQuery({ token });

  // Set initial attempt ID from reportInfo (attempts are in student.attempts)
  React.useEffect(() => {
    if (reportInfo?.student?.attempts && reportInfo.student.attempts.length > 0 && !selectedAttemptId) {
      // Select the latest attempt (last in array)
      const latestAttempt = reportInfo.student.attempts[reportInfo.student.attempts.length - 1];
      setSelectedAttemptId(latestAttempt.id);
    }
  }, [reportInfo, selectedAttemptId]);

  // Fetch detailed report data (only after we have an attempt ID)
  const { data: reportData } = useGetAssignmentReportDataQuery(
    { token, attemptId: selectedAttemptId },
    { skip: !selectedAttemptId }
  );

  // Fetch results for the selected attempt (high limit to get all results)
  const { data: resultsData } = useGetAssignmentReportResultsQuery(
    { token, attemptId: selectedAttemptId as number, limit: 999 },
    { skip: !selectedAttemptId }
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center m-auto">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-sm text-muted-foreground">{t("label-loading")}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !reportInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center m-auto">
        <Card className="w-full max-w-md border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700">{t("error-unknown-error")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-red-600">
              {t("label-assignment-report-not-found")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { assignment, student, certificate } = reportInfo;

  // Get data
  const attempts = reportInfo.student?.attempts || [];

  const openInAssignment = () => {
    if (!assignment?.id || !student?.id) return;
    const query = new URLSearchParams({ student: String(student.id) });
    if (selectedAttemptId != null) {
      query.set("attempt", String(selectedAttemptId));
    }
    window.open(
      `/assignments/${assignment.id}?${query.toString()}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-white from-slate-50 to-slate-100 border pt-0 pb-4 sm:pb-8 flex justify-center m-auto">
      <div className="w-full sm:w-[640px] sm:shrink-0 min-w-0 mx-auto">
        {/* Report Header */}
        <div className="mb-4 sm:mb-6">
          <div className="w-full bg-muted px-4 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <TrustIcon size={38} className="text-[#0059FF]"/>
                <div className="min-w-0">
                  <h1 className="text-[15px] font-medium text-gray-900 truncate leading-tight">
                    {assignment?.name || assignment?.title || t("label-report-title")}
                  </h1>
                  <p className="text-sm text-gray-500 leading-tight">
                    {t("label-report-description")}
                  </p>
                </div>
              </div>
              <ButtonGroup className="flex-shrink-0">
                <Button
                  type="button"
                  variant="outline"
                  disabled={isExporting}
                  className="gap-2 font-medium text-gray-900"
                  onClick={() => handlePrint()}
                >
                  <DownloadIcon size={16} />
                  .PDF
                </Button>
                {hasToken ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label={t("btn-open-in-assignment")}
                    title={t("btn-open-in-assignment")}
                    className="text-gray-900"
                    disabled={!assignment?.id || !student?.id}
                    onClick={openInAssignment}
                  >
                    <ExternalLink size={18} />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    aria-label={t("btn-login")}
                    title={t("btn-login")}
                    className="text-gray-900"
                    disabled={!assignment?.id || !student?.id}
                    onClick={openInAssignment}
                  >
                    <User size={18} />
                  </Button>
                )}
              </ButtonGroup>
            </div>
          </div>
        </div>
        <div className=" px-3 sm:px-4">
          {/* Main Content */}
          <div className="space-y-4 sm:space-y-6">
            {/* Certificate Card */}
            {/* {certificate && (
              <Card className="shadow-md">
                <CardHeader 
                  className="cursor-pointer p-3 sm:p-4" 
                  onClick={() => setCertificateExpanded(!certificateExpanded)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Award className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />
                      <CardTitle className="text-base sm:text-lg">
                        {t("label-assignment-certificate-title")}
                      </CardTitle>
                    </div>
                    {certificateExpanded ? (
                      <ChevronUp className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    )}
                  </div>
                  <CardDescription className="text-xs sm:text-sm">
                    {t("label-assignment-certificate-details")}
                  </CardDescription>
                </CardHeader>
                {certificateExpanded && (
                  <CardContent className="p-3 sm:p-4 pt-0">
                    <CertificateInfo certificate={certificate} student={student} />
                  </CardContent>
                )}
              </Card>
            )} */}

            {/* Student Content with Tabs */}
            {selectedAttemptId && (
              <PublicStudentContent
                student={student}
                assignment={assignment}
                reportData={reportData}
                resultsData={resultsData}
                certificate={certificate}
                attempts={attempts}
                selectedAttemptId={selectedAttemptId}
                onAttemptChange={setSelectedAttemptId}
              />
            )}

            {/* Certificate-only view: no attempts available (e.g. show_report disabled) */}
            {!selectedAttemptId && certificate && (
              <Card className="shadow-md">
                <CardHeader className="p-3 sm:p-4">
                  <CardTitle className="text-base sm:text-lg">
                    {t("label-assignment-certificate-title")}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 sm:p-4 pt-0">
                  <CertificateInfo certificate={certificate} student={student} />
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Hidden print view for PDF export */}
        {selectedAttemptId && (
          <ReportPrintView
            ref={printRef}
            student={student}
            assignment={assignment}
            reportData={reportData}
            resultsData={resultsData}
            certificate={certificate}
            selectedAttemptId={selectedAttemptId}
          />
        )}
      </div>
    </div>
  );
};

export default ReportPage;
